import { useEffect, useState } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { UPDATE_CHECK_MS } from '@/constants/app';

/**
 * Telling him a new version has arrived.
 *
 * The app serves itself from a cache so it opens instantly and works with no
 * connection. The cost is that a deployed change does not appear until the
 * page is loaded again, and nothing used to say so — which looked exactly like
 * the change never shipped.
 *
 * So: check for a new build on a timer and whenever he comes back to the app,
 * and when one is ready, say so and offer the reload. Deliberately not
 * automatic. A reload in the middle of a typed answer would lose it, and
 * surprising someone mid-question to save them one tap is a bad trade.
 */
export function UpdatePrompt() {
  const [ready, setReady] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [apply, setApply] = useState<(() => Promise<void>) | null>(null);

  useEffect(() => {
    const update = registerSW({
      immediate: true,
      onNeedRefresh() {
        setReady(true);
      },
      onRegisteredSW(_url, registration) {
        if (!registration) return;

        const check = () => void registration.update().catch(() => {});
        const timer = window.setInterval(check, UPDATE_CHECK_MS);
        // Coming back to the app is the moment a new version is most likely to
        // be waiting, and the moment he is most likely to want it.
        const onVisible = () => {
          if (document.visibilityState === 'visible') check();
        };
        document.addEventListener('visibilitychange', onVisible);

        cleanup = () => {
          window.clearInterval(timer);
          document.removeEventListener('visibilitychange', onVisible);
        };
      },
    });

    let cleanup: (() => void) | null = null;
    setApply(() => () => update(true));

    return () => cleanup?.();
  }, []);

  if (!ready) return null;

  return (
    <div className="update-prompt" role="status">
      <span>A newer version of Chem Bank is ready.</span>
      <button
        className="btn btn--sm btn--primary"
        disabled={reloading}
        onClick={() => {
          setReloading(true);
          void apply?.();
        }}
      >
        {reloading ? 'Updating…' : 'Update now'}
      </button>
      <button className="icon-btn" aria-label="Not now" onClick={() => setReady(false)}>
        ✕
      </button>
    </div>
  );
}
